import { CalendarClock } from 'lucide-react';
import { MoldOrder } from '../../types/mold';
import { isFinalStep } from '../MoldDetail/utils';
import { formatShortThaiDate, getDaysSince, getRemainingDaysInThai } from '../../utils/dateUtils';
import { getCurrentStatusStartDate, isMoldAcceptanceOrder } from '../utils/moldOrder';
import { useAppState } from '@/context/AppStateContext';

const MoldOrderDueDateLabelForMoldAcceptance = ({
    moldOrder
}: {
    moldOrder: MoldOrder
}) => {
    const isPendingAcceptance = moldOrder.status === 'pending_arrival';
    const displayDate = moldOrder.orderDetail.form.moldDate?.arrivalDate || moldOrder.orderDetail.expectedArrivalDate;
    if (!moldOrder.orderDetail.expectedArrivalDate) {
        return <div className='pt-0 pb-2 text-xs min-h-7'></div>;
    }
    if (isPendingAcceptance) {
        return (
            <div className='flex justify-start items-start pt-0 pb-2 text-xs gap-1 text-gray-600'>
                <CalendarClock size={12} className='text-gray-600' />
                <span>กำหนดรับเข้า: {formatShortThaiDate(moldOrder.orderDetail.expectedArrivalDate)}</span>
            </div>
        );
    }
    return (
        <div className='flex justify-start items-start pt-0 pb-2 text-xs gap-1 text-gray-500'>
            <CalendarClock size={12} className='text-gray-600' />
            <span>รับเข้าเมื่อ: {formatShortThaiDate(displayDate)}</span>
        </div>
    );
}

const MoldOrderDueDateLabel = ({
    moldOrder
}: {
    moldOrder: MoldOrder
}) => {
    const { getStatusDueDateBehavior } = useAppState();
    const isInFinalState = isFinalStep(moldOrder.status);
    if (isMoldAcceptanceOrder(moldOrder)) {
        return <MoldOrderDueDateLabelForMoldAcceptance moldOrder={moldOrder} />;
    }

    // When waiting on an external party (e.g. customer review), the SK due-date
    // clock is paused: suppress its delay warning and optionally show a count of
    // days spent in this status, escalating to amber past the configured threshold.
    const dueDateBehavior = getStatusDueDateBehavior(moldOrder.status, moldOrder.type);
    if (dueDateBehavior?.suppressDueDateWarning) {
        const { responseReminder } = dueDateBehavior;
        if (responseReminder) {
            const statusStart = getCurrentStatusStartDate(moldOrder);
            const days = statusStart ? getDaysSince(statusStart) : 0;
            const reminderColor = days >= responseReminder.warnAfterDays ? 'text-amber-600' : 'text-gray-600';
            const reminderText = (responseReminder.label ?? 'รอ {days} วัน').replace('{days}', String(days));
            return (
                <div className={`flex justify-start items-start pt-0 pb-2 text-xs gap-1 ${reminderColor}`}>
                    <CalendarClock size={12} className={reminderColor} />
                    <span>{reminderText}</span>
                </div>
            );
        }
        return <div className='pt-0 pb-2 text-xs min-h-7'></div>;
    }

    const dueDate = moldOrder.orderDetail.dueDate ?? moldOrder.orderDetail.expectedArrivalDate;

    const getDisplayStatusColor = (status: 'red' | 'yellow' | 'green') => {
        if (isInFinalState) return 'text-gray-600';
        return status === 'red' ? 'text-red-600' : status === 'yellow' ? 'text-yellow-600' : 'text-green-600';
    }

    if (!dueDate) {
        return <div className='pt-0 pb-2 text-xs min-h-7'></div>;
    }

    const { displayText, status } = getRemainingDaysInThai(dueDate);
    const displayDueDate = formatShortThaiDate(dueDate);


    const displayShiftCompleted = moldOrder.orderDetail.shiftCompleted ? `(กะ ${moldOrder.orderDetail.shiftCompleted})` : '';
    const statusColor = getDisplayStatusColor(status);
    return (
        <div className={`flex justify-start items-start pt-0 pb-2 text-xs gap-1 ${statusColor}`}>
            <CalendarClock size={12} className={statusColor} />
            {isInFinalState ?
                (<span>กำหนดเสร็จ: {displayDueDate} {displayShiftCompleted}</span>) :
                (<span>{displayText} {displayShiftCompleted}</span>)}
        </div>
    );
}

export default MoldOrderDueDateLabel;